import type { ApsOcrExtraction } from '../types/apsOcr';
import {
    formatExtractedFieldValue,
    normalizeApsOcrExtraction,
    parseStoredApsOcrExtraction,
} from './apsOcrPrefill';
import {
    compactLegalDocPrefills,
    getOtherPartyNamesFieldKey,
    getOtherPartyNamesFromPrefills,
} from './legalDocPrefills';
import { APS_MATTER_FIELDS, getMatterField, getMatterFieldApsKey } from './matterFields';

export interface ProspectFieldSources {
    retainerAmount?: number | string | null;
    subjectAddress?: string | null;
    closingDate?: string | null;
    purchasePrice?: number | string | null;
    legalDocPrefills?: Record<string, string> | null;
    apsOcrExtraction?: unknown;
}

/**
 * The scan's non-empty values as editable strings, keyed by matter field key. Currency values
 * are plain numbers (`1700000.00`), as currency inputs only accept those.
 */
export function getApsFieldValues(extraction: ApsOcrExtraction): Record<string, string> {
    const values: Record<string, string> = {};
    for (const field of APS_MATTER_FIELDS) {
        const apsKey = getMatterFieldApsKey(field);
        const formatted = apsKey ? formatExtractedFieldValue(extraction, apsKey) : '';
        const value = field.type === 'currency' ? formatted.replace(/[$,\s]/g, '') : formatted;
        if (value) {
            values[field.key] = value;
        }
    }
    return values;
}

/**
 * Field values after applying a new scan's values. Values the previous scan supplied that the
 * user never changed belong to that document, so they're dropped first rather than kept where the
 * new scan found nothing.
 */
export function mergeScanFieldValues(
    current: Record<string, string>,
    previousScanValues: Record<string, string>,
    scanValues: Record<string, string>,
): Record<string, string> {
    const kept = Object.entries(current).filter(
        ([key, value]) => previousScanValues[key] !== value,
    );
    return { ...Object.fromEntries(kept), ...scanValues };
}

/**
 * The scan with every APS field set from values keyed by matter field key. Missing values clear.
 * Inverse of `getApsFieldValues`.
 */
export function applyFieldValuesToExtraction(
    extraction: ApsOcrExtraction,
    values: Record<string, string>,
): ApsOcrExtraction {
    const raw: Record<string, unknown> = { ...extraction };
    for (const field of APS_MATTER_FIELDS) {
        const apsKey = getMatterFieldApsKey(field);
        if (apsKey) {
            const value = values[field.key] ?? '';
            raw[apsKey] = field.type === 'list' ? value.split(',') : value;
        }
    }
    return normalizeApsOcrExtraction(raw);
}

/**
 * A prospect's field values, keyed by matter field key, or by tag for names that are not a
 * matter field. When sources have a value for the same field, the typed override columns win
 * over the legal document values, which win over the APS scan.
 */
export function getProspectFieldValues(
    sources: ProspectFieldSources,
    matterType: string | null | undefined,
): Record<string, string> {
    const extraction = parseStoredApsOcrExtraction(sources.apsOcrExtraction);
    const values: Record<string, string> = extraction ? getApsFieldValues(extraction) : {};

    const setValue = (name: string, raw: number | string | null | undefined) => {
        const value = raw == null ? '' : String(raw).trim();
        if (value) {
            values[getMatterField(name)?.key ?? name] = value;
        }
    };

    const legalDocPrefills = compactLegalDocPrefills(sources.legalDocPrefills);
    for (const [tag, value] of Object.entries(legalDocPrefills)) {
        setValue(tag, value);
    }
    setValue(
        getOtherPartyNamesFieldKey(matterType),
        getOtherPartyNamesFromPrefills(legalDocPrefills, matterType),
    );

    setValue('retainerAmount', sources.retainerAmount);
    setValue('subjectAddress', sources.subjectAddress);
    setValue('closingDate', sources.closingDate);
    setValue('purchasePrice', sources.purchasePrice);

    return values;
}
